// Contient les fonctions correspondant a chaque endpoint, renvoyant une Response brute
// Chaque fonction fait appel à une fonction fetch commune

import LoggerService,{formatTrace} from '../LoggerService';

import {I_ACCESS_TOKEN_PARAMS,I_REFRESH_TOKEN_PARAMS,_customFetch, getFetchGETOptions} from './endpoints/_base';
import activityTypesEndpoints from './endpoints/activityTypes';
import taskTypesEndpoints from './endpoints/taskTypes';
import skillsEndpoints from './endpoints/skills';
import billingCustomersEndpoints from './endpoints/billingCustomers';
import usersEndpoints from './endpoints/users';
import collaboratorsEndpoints from './endpoints/collaborators';
import ooodaysEndpoints from './endpoints/ooodays';
import customersEndpoints from './endpoints/customers';
import chargesEndpoints from './endpoints/charges';
import activitiesEndpoints from './endpoints/activities';
import chargesModelsEndpoints from './endpoints/charges-models';
import planificationEndpoints from './endpoints/planification';
import logsEndpoints from './endpoints/logs';
import filtersEndpoints from './endpoints/filters';
import chargesReportEndpoints from './endpoints/charges-report';
import alertsEndpoints from './endpoints/alerts';
import microsoftEndpoints from './endpoints/microsoft';
import organisationsEndpoints from './endpoints/organisations';
import APP_CONFIG from 'config';

const ENDPOINT_HELLO = '/hello';
const ENDPOINT_LOGIN =  '/login';
const ENDPOINT_LOGOUT =  '/logout';
const ENDPOINT_REFRESH =  '/refresh';
const ENDPOINT_USERS_ME =  '/users/me';
const ENDPOINT_FORGOT_PASSWORD = '/forgot-password';
const ENDPOINT_RESET_PASSWORD = '/reset-password';

const logger = LoggerService.getInstance();

/**
 * Requete le endpoint hello
 * Récupère les configurations du service Osac Planif gérées par le backend
 * @returns {Promise<Response>}
 */
export const getHelloResponse = ():Promise<Response> => {
  logger.trace(...formatTrace('ApiEndpoints - getHelloResponse...'));
  const params = {
    method: 'GET',
    headers: {
      'X-API-KEY': APP_CONFIG.API_KEY,
      'Content-Type': 'application/json'
    }
  };
  const URL = ENDPOINT_HELLO + APP_CONFIG.ENDPOINT_SUFFIX;
  return _customFetch(URL,params);
};


interface I_LOGIN_PARAMS {
  email: string,
  password: string,
}
/**
 * Requete le endpoint login
 * Récupère les token du user à partir du login/mdp
 * @param {I_LOGIN_PARAMS} obj
 * @returns {Promise<Response>}
 */
export const getLoginResponse = ({email,password}:I_LOGIN_PARAMS):Promise<Response> => {
  logger.trace(...formatTrace('ApiEndpoints - getLoginResponse...'));
  const params = {
    method: 'POST',
    headers: {
      'X-API-KEY': APP_CONFIG.API_KEY,
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: `username=${encodeURIComponent(email)}&password=${encodeURIComponent(password)}`,
  };
  const URL = ENDPOINT_LOGIN + APP_CONFIG.ENDPOINT_SUFFIX;
  return _customFetch(URL,params);
};

/**
 * Requete le endpoint refreshtoken
 * Récupère les token du user à partir du refresh token
 * @param {I_REFRESH_TOKEN_PARAMS} obj
 * @returns {Promise<Response>}
 */
export const postRefreshToken = async ({refreshToken}:I_REFRESH_TOKEN_PARAMS):Promise<Response> => {
  logger.trace(...formatTrace('ApiEndpoints - postRefreshToken...'));
  const params = {
    method: 'POST',
    headers: {
      'X-API-KEY': APP_CONFIG.API_KEY,
      'Content-Type': 'application/json',
      'Authorization': 'Bearer '+refreshToken
    },
  };
  const URL = ENDPOINT_REFRESH + APP_CONFIG.ENDPOINT_SUFFIX;
  return _customFetch(URL,params);
};

/**
 * Requete le endpoint logout
 * Demande à l'API d'invalider les tokens
 * @param obj
 * @returns
 */
export const getLogoutResponse = async ({accessToken}:I_ACCESS_TOKEN_PARAMS):Promise<Response> => {
  logger.trace(...formatTrace('ApiEndpoints - getLogoutResponse...'));
  const params = {
    method: 'POST',
    headers: {
      'X-API-KEY': APP_CONFIG.API_KEY,
      'Content-Type': 'application/json',
      'Authorization': 'Bearer '+accessToken
    },
  };
  const URL = ENDPOINT_LOGOUT + APP_CONFIG.ENDPOINT_SUFFIX + `?redirect_uri=${window.location.origin}`;
  return _customFetch(URL,params);
};


/**
 * Requete le endpoint user/me
 * Récupère les infos du user
 * @param {I_ACCESS_TOKEN_PARAMS} obj
 * @returns {Promise<Response>}
 */
export const getUsersMeResponse = async ({accessToken}:I_ACCESS_TOKEN_PARAMS):Promise<Response> => {
  logger.trace(...formatTrace('ApiEndpoints - getUsersMeResponse...'));
  const options = getFetchGETOptions({accessToken});
  const URL = ENDPOINT_USERS_ME + APP_CONFIG.ENDPOINT_SUFFIX;
  return _customFetch(URL,options);
};


interface I_FORGOT_PWD_PARAMS {
  email: string
}

/**
 * Sends an email to reset password
 * @param email
 */
export const postForgotPassword = async ({email}: I_FORGOT_PWD_PARAMS) => {
  logger.trace(...formatTrace('ApiEndpoints - postResetPasswordEmail...'));

  const params = {
    method: 'POST',
    headers: {
      'X-API-KEY': APP_CONFIG.API_KEY,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      username: email
    })
  };
  const URL = ENDPOINT_FORGOT_PASSWORD + APP_CONFIG.ENDPOINT_SUFFIX;
  return _customFetch(URL,params);
}


interface I_RESET_PWD_PARAMS {
  token: string
  password: string
}
export const postResetPassword = async ({password, token}: I_RESET_PWD_PARAMS) => {
  logger.trace(...formatTrace('ApiEndpoints - postResetPassword...'));
  const params = {
    method: 'POST',
    headers: {
      'X-API-KEY': APP_CONFIG.API_KEY,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      new_password: password,
      reset_token: token
    })
  };
  const URL = ENDPOINT_RESET_PASSWORD + APP_CONFIG.ENDPOINT_SUFFIX;
  return _customFetch(URL,params);
}


// ---------------------------------
// --- ACTIVITY TYPES
// ---------------------------------

export const getActivityTypesResponse = activityTypesEndpoints.get;
export const postActivityTypeResponse = activityTypesEndpoints.post;
export const deleteActivityTypeResponse = activityTypesEndpoints.remove;
export const putActivityTypeResponse = activityTypesEndpoints.put;

// ---------------------------------
// --- TASK TYPES
// ---------------------------------

export const getTaskTypesResponse = taskTypesEndpoints.get;
export const postTaskTypeResponse = taskTypesEndpoints.post;
export const deleteTaskTypeResponse = taskTypesEndpoints.remove;
export const putTaskTypeResponse = taskTypesEndpoints.put;

// ---------------------------------
// --- SKILLS
// ---------------------------------

export const getSkillsResponse = skillsEndpoints.get;
export const postSkillResponse = skillsEndpoints.post;
export const deleteSkillResponse = skillsEndpoints.remove;
export const putSkillResponse = skillsEndpoints.put;

// ---------------------------------
// --- BILLING CUSTOMERS
// ---------------------------------

export const getBillingCustomersResponse = billingCustomersEndpoints.get;
export const postBillingCustomerResponse = billingCustomersEndpoints.post;
export const deleteBillingCustomerResponse = billingCustomersEndpoints.remove;
export const putBillingCustomerResponse = billingCustomersEndpoints.put;
export const importBillingCustomersResponse = billingCustomersEndpoints.importList;

// ---------------------------------
// --- USERS
// ---------------------------------

export const getUsersResponse = usersEndpoints.get;
export const postUserResponse = usersEndpoints.post;
export const deleteUserResponse = usersEndpoints.remove;
export const putUserResponse = usersEndpoints.put;
export const getUserResponse = usersEndpoints.getItem;

// ---------------------------------
// --- COLLABORATEURS
// ---------------------------------

export const getCollaboratorsResponse = collaboratorsEndpoints.get;
export const postCollaboratorResponse = collaboratorsEndpoints.post;
export const deleteCollaboratorResponse = collaboratorsEndpoints.remove;
export const putCollaboratorResponse = collaboratorsEndpoints.put;
export const getCollaboratorResponse = collaboratorsEndpoints.getItem;
export const importCollaboratorsResponse = collaboratorsEndpoints.importList;

// ---------------------------------
// --- OOODAYS collabs
// ---------------------------------

export const getOOODaysResponse = ooodaysEndpoints.get;
export const putOOODayResponse = ooodaysEndpoints.put;

// ---------------------------------
// --- ("AUDITED") CUSTOMERS
// ---------------------------------

export const getCustomersResponse = customersEndpoints.get;
export const postCustomerResponse = customersEndpoints.post;
export const deleteCustomerResponse = customersEndpoints.remove;
export const putCustomerResponse = customersEndpoints.put;
export const getCustomerResponse = customersEndpoints.getItem;
export const importCustomersResponse = customersEndpoints.importList;

// ---------------------------------
// --- CHARGES
// ---------------------------------

export const getChargesResponse = chargesEndpoints.get;
export const postChargeResponse = chargesEndpoints.post;
export const deleteChargeResponse = chargesEndpoints.remove;
export const putChargeResponse = chargesEndpoints.put;
export const getChargeResponse = chargesEndpoints.getItem;
export const importChargesResponse = chargesEndpoints.importList;
export const exportChargesResponse = chargesEndpoints.exportList;


// ---------------------------------
// --- ACTIVITIES
// ---------------------------------

export const getActivitiesResponse = activitiesEndpoints.get;
export const postActivityResponse = activitiesEndpoints.post;
export const deleteActivityResponse = activitiesEndpoints.remove;
export const putActivityResponse = activitiesEndpoints.put;
export const getActivityResponse = activitiesEndpoints.getItem;
export const exportActivitiesResponse = activitiesEndpoints.exportActivities;

// ---------------------------------
// --- CHARGES MODELS
// ---------------------------------

export const getChargesModelsResponse = chargesModelsEndpoints.get;
export const postChargeModelResponse = chargesModelsEndpoints.post;
export const deleteChargeModelResponse = chargesModelsEndpoints.remove;
export const putChargeModelResponse = chargesModelsEndpoints.put;
export const getChargeModelResponse = chargesModelsEndpoints.getItem;

// ---------------------------------
// --- PLANIFICATION
// ---------------------------------

export const getPlanificationTotalsResponse = planificationEndpoints.getTotals;
export const getPlanificationByCollaboratorsResponse = planificationEndpoints.getByCollaborators;
export const getPlanificationByCustomersResponse = planificationEndpoints.getByCustomers;
export const getPlanificationByActivityTypesResponse = planificationEndpoints.getByActivityTypes;
export const getPlanificationBySkillsResponse = planificationEndpoints.getBySkills;
export const getPlanificationByFunctionsResponse = planificationEndpoints.getByFunctions;
export const getPlanificationByOrganisationsResponse = planificationEndpoints.getByOrganisations;

// ---------------------------------
// --- LOGS
// ---------------------------------

export const getLogsChargeResponse = logsEndpoints.getCharge;
export const getLogsActivityResponse = logsEndpoints.getActivity;
export const getLogsCollaboratorResponse = logsEndpoints.getCollaborator;
export const getLogsCustomerResponse = logsEndpoints.getCustomer;

// ---------------------------------
// --- FILTERS
// ---------------------------------

export const getFiltersResponse = filtersEndpoints.getFilters;
export const postFilterResponse = filtersEndpoints.postFilter;
export const deleteFilterResponse = filtersEndpoints.deleteFilter;
export const putFilterResponse = filtersEndpoints.putFilter;

// ---------------------------------
// --- CHARGES REPORT
// ---------------------------------

export const getChargesWithoutPaginationResponse = chargesReportEndpoints.getList;
export const postChargesReportResponse = chargesReportEndpoints.post;
export const getChargesReportResponse = chargesReportEndpoints.get;
export const cancelChargesReportResponse = chargesReportEndpoints.postCancel;
export const validateChargesReportResponse = chargesReportEndpoints.postValidate;

// ---------------------------------
// --- ALERTS
// ---------------------------------

export const getAlertsResponse = alertsEndpoints.get;
export const getPrefsAlertsResponse = alertsEndpoints.getPref;
export const setPrefAlertsResponse = alertsEndpoints.setPref;
export const acknowledgeAlertResponse = alertsEndpoints.acknowledge;

// ---------------------------------
// --- MICROSOFT
// ---------------------------------

export const getMicrosoftLinkResponse = microsoftEndpoints.getLink;
export const getMicrosoftTokensResponse = microsoftEndpoints.connect;

// ---------------------------------
// --- ORGANISATIONS
// ---------------------------------

export const getOrganisationsResponse = organisationsEndpoints.get;
